"use client";
import { useFormContext } from "react-hook-form";
import React from "react";
import { InputField } from "@/forms/InputField";
import { FormGroup } from "@/forms/FormGroup";
import {
  AddressType,
  POSTAL_CODE_REGEX,
} from "@cappelendamm/e-commerce-services";
import { Typography } from "@cappelendamm/design-system/react";
import { getPostalCodeInformation } from "@/postal-codes/getPostalInformation";

export type AddressForm = {
  firstName: string;
  lastName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

type AddressFieldsProps = {
  type: AddressType;
  isDisabled: boolean;
  title: string;
  hideNameFields?: boolean;
};

// AddressType is here converted to a string because typescript would parse the dynamic field values as never
export type AddressFieldsForm = Record<`${AddressType}`, AddressForm>;

export const AddressFields = ({
  type: addressType,
  isDisabled,
  title,
  hideNameFields = false,
}: AddressFieldsProps) => {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext<AddressFieldsForm>();
  const [postalDeliveryCode] = watch([
    "delivery.postalCode",
    "billing.postalCode",
  ]);
  return (
    <FormGroup isDisabled={isDisabled}>
      <Typography variant="title-md">{title}</Typography>
      {!hideNameFields && (
        <>
          <InputField
            name={`${addressType}.firstName`}
            label="Fornavn"
            register={register}
            error={errors[addressType]?.firstName}
            requiredMessage="Du må skrive inn et fornavn"
            autoComplete="given-name"
            sensitive
          />
          <InputField
            name={`${addressType}.lastName`}
            label="Etternavn"
            register={register}
            error={errors[addressType]?.lastName}
            requiredMessage="Du må skrive inn et etternavn"
            autoComplete="family-name"
            sensitive
          />
        </>
      )}
      <InputField
        name={`${addressType}.street`}
        label="Adresse"
        register={register}
        error={errors[addressType]?.street}
        requiredMessage="Du må skrive inn en adresse"
        autoComplete="street-address"
        sensitive
      />
      <div className="flex items-start gap-4">
        <InputField
          className="w-[8rem] sm:w-[10rem]"
          name={`${addressType}.postalCode`}
          label="Postnummer"
          register={register}
          error={errors[addressType]?.postalCode}
          validate={async () => {
            const postalCodeInfo =
              await getPostalCodeInformation(postalDeliveryCode);
            if (postalCodeInfo) return true;
            return "Ugyldig postnummer";
          }}
          pattern={{
            value: POSTAL_CODE_REGEX,
            message: "Postnummeret må være 4 siffer",
          }}
          requiredMessage="Du må skrive inn et postnummer"
          autoComplete="postal-code"
          type="number"
          inputMode="numeric"
          sensitive
        />
        <InputField
          className="grow"
          inputClassName="uppercase"
          name={`${addressType}.city`}
          label="Poststed"
          register={register}
          error={errors[addressType]?.city}
          requiredMessage="Du må skrive inn et poststed"
          sensitive
        />
      </div>
    </FormGroup>
  );
};
